import { useMemo, type ReactNode } from "react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { ApiDatatable, type ApiDatatableBaseParams } from "@/components/data-table";
import {
  getMenuItemColumns,
  menuItemsToRows,
  MenuItemTile,
  type MenuItemRow,
} from "@/components/recipes/tables";
import { menuItemRequests } from "@/lib/api/requests/menu-items";
import { getApiErrorMessage } from "@/lib/api/api";
import type { ListViewMode } from "@/lib/api/requests/ui-preferences";
import { useDeleteMenuItem } from "@/hooks/menu-items";

export type MenuItemsTableParams = ApiDatatableBaseParams & {
  /** The menu category the list is filtered to; unset is all of them. */
  categoryId?: number;
};

interface MenuItemsTableProps<TParams extends MenuItemsTableParams> {
  searchParams: TParams;
  onNavigate: (updater: (prev: TParams) => TParams) => void;
  /** Concept the list is scoped to. */
  companyId?: number;
  enabled?: boolean;
  /** Row or tile click. Omit to make rows inert (e.g. while selecting). */
  onOpen?: (id: number) => void;
  /** The row menu's "Edit item". */
  onEdit: (id: number) => void;
  /** Whether the viewer may edit and delete. Without it rows carry no actions menu. */
  canWrite?: boolean;
  /** Opt-in: a stored table or tile choice. Omit both and the table keeps its own. */
  viewMode?: ListViewMode;
  onViewModeChange?: (mode: ListViewMode) => void;
  /** Adds the checkbox column. */
  selectable?: boolean;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  onDataChange?: (rows: MenuItemRow[]) => void;
  toolbarExtra?: ReactNode;
  emptyMessage: string;
  emptyState?: ReactNode;
}

/**
 * The menu item list: columns, row actions, delete and the paged query.
 * Shared by the Menus module and the Recipes screen's Menu Items tab, which
 * differ only in what a row click does and what sits around the table.
 */
export function MenuItemsTable<TParams extends MenuItemsTableParams>({
  searchParams,
  onNavigate,
  companyId,
  enabled = true,
  onOpen,
  onEdit,
  canWrite = true,
  viewMode,
  onViewModeChange,
  selectable = false,
  rowSelection,
  onRowSelectionChange,
  onDataChange,
  toolbarExtra,
  emptyMessage,
  emptyState,
}: MenuItemsTableProps<TParams>) {
  const { t, i18n } = useLingui();
  const deleteMenuItem = useDeleteMenuItem();

  const columns = useMemo(
    () =>
      getMenuItemColumns(i18n, {
        selectable,
        callbacks: canWrite
          ? {
              onEdit: (row) => onEdit(Number(row.id)),
              onDelete: (row) =>
                deleteMenuItem.mutate(Number(row.id), {
                  onSuccess: () => toast.success(t`Menu item deleted`),
                  onError: (error) =>
                    toast.error(getApiErrorMessage(error, t`Failed to delete menu item`)),
                }),
            }
          : undefined,
      }),
    [i18n, selectable, canWrite, onEdit, deleteMenuItem, t],
  );

  return (
    <ApiDatatable
      searchParams={searchParams}
      onNavigate={onNavigate}
      queryKey={["menu-items", "list", companyId ?? null]}
      queryFn={(params) =>
        menuItemRequests
          .list({
            page: params.page,
            limit: params.pageSize,
            search: params.search,
            sortBy: params.sortBy as never,
            sortOrder: params.sortOrder,
            companyId,
            categoryId: params.categoryId,
          })
          .then((res) => ({ data: menuItemsToRows(res.data, i18n), meta: res.meta }))
      }
      columns={columns}
      renderTile={(item) => (
        <MenuItemTile item={item} onClick={onOpen ? () => onOpen(Number(item.id)) : undefined} />
      )}
      // The toolbar calls its tile view "item".
      viewMode={viewMode ? (viewMode === "tile" ? "item" : "table") : undefined}
      onViewModeChange={
        onViewModeChange ? (mode) => onViewModeChange(mode === "item" ? "tile" : "table") : undefined
      }
      getRowId={(row) => String(row.id)}
      onRowClick={onOpen ? (row) => onOpen(Number(row.id)) : undefined}
      toolbarExtra={toolbarExtra}
      searchPlaceholder={t`Search menu items…`}
      searchAriaLabel={t`Search menu items`}
      emptyMessage={emptyMessage}
      emptyState={emptyState}
      enabled={enabled}
      enableRowSelection={selectable}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      onDataChange={onDataChange}
    />
  );
}
